import { isDemoMode, db } from '../firebase';
import type { Enrollment } from '../types';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';

const DEMO_ENROLLMENTS_KEY = 'demo_enrollments';

// ── Demo helpers ──

const getDemoEnrollments = (): Enrollment[] => {
  const stored = localStorage.getItem(DEMO_ENROLLMENTS_KEY);
  if (!stored) return [];
  try {
    return JSON.parse(stored).map((e: any) => ({
      ...e,
      startDate: new Date(e.startDate),
      createdAt: new Date(e.createdAt),
      updatedAt: new Date(e.updatedAt),
      deadlineOverrides: e.deadlineOverrides || {},
    }));
  } catch {
    return [];
  }
};

const saveDemoEnrollments = (enrollments: Enrollment[]) => {
  localStorage.setItem(DEMO_ENROLLMENTS_KEY, JSON.stringify(enrollments));
};

// ── Firestore helpers ──

const firestoreToEnrollment = (id: string, data: any): Enrollment => ({
  id,
  userId: data.userId,
  skillTreeId: data.skillTreeId,
  startDate: data.startDate?.toDate?.() || new Date(data.startDate),
  deadlineOverrides: data.deadlineOverrides || {},
  createdAt: data.createdAt?.toDate?.() || new Date(),
  createdBy: data.createdBy || '',
  updatedAt: data.updatedAt?.toDate?.() || new Date(),
  deletedAt: data.deletedAt?.toDate?.() || null,
  deletedBy: data.deletedBy || null,
});

// ── Service ──

class EnrollmentService {
  /** Get all enrollments (excludes soft-deleted by default) */
  async getAll(includeDeleted = false): Promise<Enrollment[]> {
    if (isDemoMode) {
      const all = getDemoEnrollments();
      return includeDeleted ? all : all.filter((e) => !e.deletedAt);
    }
    if (!db) return [];

    const snapshot = await getDocs(collection(db, 'enrollments'));
    const all = snapshot.docs.map((d) => firestoreToEnrollment(d.id, d.data()));
    return includeDeleted ? all : all.filter((e) => !e.deletedAt);
  }

  /** Get enrollments for a specific user (excludes soft-deleted by default) */
  async getByUser(userId: string, includeDeleted = false): Promise<Enrollment[]> {
    if (isDemoMode) {
      const all = getDemoEnrollments().filter((e) => e.userId === userId);
      return includeDeleted ? all : all.filter((e) => !e.deletedAt);
    }
    if (!db) return [];

    const q = query(collection(db, 'enrollments'), where('userId', '==', userId));
    const snapshot = await getDocs(q);
    const all = snapshot.docs.map((d) => firestoreToEnrollment(d.id, d.data()));
    return includeDeleted ? all : all.filter((e) => !e.deletedAt);
  }

  /** Get enrollments for a specific skill tree (excludes soft-deleted by default) */
  async getByTree(treeId: string, includeDeleted = false): Promise<Enrollment[]> {
    if (isDemoMode) {
      const all = getDemoEnrollments().filter((e) => e.skillTreeId === treeId);
      return includeDeleted ? all : all.filter((e) => !e.deletedAt);
    }
    if (!db) return [];

    const q = query(collection(db, 'enrollments'), where('skillTreeId', '==', treeId));
    const snapshot = await getDocs(q);
    const all = snapshot.docs.map((d) => firestoreToEnrollment(d.id, d.data()));
    return includeDeleted ? all : all.filter((e) => !e.deletedAt);
  }

  /** Create a new enrollment. Returns the new enrollment. */
  async create(
    userId: string,
    skillTreeId: string,
    startDate: Date,
    createdBy: string,
  ): Promise<Enrollment> {
    const now = new Date();
    const enrollment: Enrollment = {
      id: '', // set below
      userId,
      skillTreeId,
      startDate,
      deadlineOverrides: {},
      createdAt: now,
      createdBy,
      updatedAt: now,
      deletedAt: null,
      deletedBy: null,
    };

    if (isDemoMode) {
      const all = getDemoEnrollments();
      // Prevent duplicate
      if (all.some((e) => e.userId === userId && e.skillTreeId === skillTreeId)) {
        throw new Error('Student is already enrolled in this skill tree');
      }
      enrollment.id = `enr_${Date.now()}`;
      all.push(enrollment);
      saveDemoEnrollments(all);
      return enrollment;
    }

    if (!db) throw new Error('Database not available');

    // Check for duplicate
    const existing = await this.getByUser(userId);
    if (existing.some((e) => e.skillTreeId === skillTreeId)) {
      throw new Error('Student is already enrolled in this skill tree');
    }

    const docRef = doc(collection(db, 'enrollments'));
    enrollment.id = docRef.id;
    await setDoc(docRef, {
      userId,
      skillTreeId,
      startDate: Timestamp.fromDate(startDate),
      deadlineOverrides: {},
      createdAt: Timestamp.fromDate(now),
      createdBy,
      updatedAt: Timestamp.fromDate(now),
    });

    return enrollment;
  }

  /** Update start date */
  async updateStartDate(enrollmentId: string, startDate: Date): Promise<void> {
    if (isDemoMode) {
      const all = getDemoEnrollments();
      const idx = all.findIndex((e) => e.id === enrollmentId);
      if (idx < 0) throw new Error('Enrollment not found');
      all[idx].startDate = startDate;
      all[idx].updatedAt = new Date();
      saveDemoEnrollments(all);
      return;
    }

    if (!db) throw new Error('Database not available');
    await updateDoc(doc(db, 'enrollments', enrollmentId), {
      startDate: Timestamp.fromDate(startDate),
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  /** Set a deadline override for a qualification */
  async setDeadlineOverride(enrollmentId: string, qualId: string, daysFromStart: number): Promise<void> {
    if (isDemoMode) {
      const all = getDemoEnrollments();
      const idx = all.findIndex((e) => e.id === enrollmentId);
      if (idx < 0) throw new Error('Enrollment not found');
      all[idx].deadlineOverrides[qualId] = daysFromStart;
      all[idx].updatedAt = new Date();
      saveDemoEnrollments(all);
      return;
    }

    if (!db) throw new Error('Database not available');
    const ref = doc(db, 'enrollments', enrollmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Enrollment not found');
    const overrides = snap.data().deadlineOverrides || {};
    overrides[qualId] = daysFromStart;
    await updateDoc(ref, {
      deadlineOverrides: overrides,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  /** Clear a deadline override (revert to default) */
  async clearDeadlineOverride(enrollmentId: string, qualId: string): Promise<void> {
    if (isDemoMode) {
      const all = getDemoEnrollments();
      const idx = all.findIndex((e) => e.id === enrollmentId);
      if (idx < 0) throw new Error('Enrollment not found');
      delete all[idx].deadlineOverrides[qualId];
      all[idx].updatedAt = new Date();
      saveDemoEnrollments(all);
      return;
    }

    if (!db) throw new Error('Database not available');
    const ref = doc(db, 'enrollments', enrollmentId);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error('Enrollment not found');
    const overrides = snap.data().deadlineOverrides || {};
    delete overrides[qualId];
    await updateDoc(ref, {
      deadlineOverrides: overrides,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  /** Soft-delete an enrollment (preserves data for recovery) */
  async remove(enrollmentId: string, deletedBy: string): Promise<void> {
    if (isDemoMode) {
      const all = getDemoEnrollments();
      const idx = all.findIndex((e) => e.id === enrollmentId);
      if (idx >= 0) {
        all[idx].deletedAt = new Date();
        all[idx].deletedBy = deletedBy;
        saveDemoEnrollments(all);
      }
      return;
    }

    if (!db) throw new Error('Database not available');
    await updateDoc(doc(db, 'enrollments', enrollmentId), {
      deletedAt: Timestamp.fromDate(new Date()),
      deletedBy,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  /** Restore a soft-deleted enrollment */
  async restore(enrollmentId: string): Promise<void> {
    if (isDemoMode) {
      const all = getDemoEnrollments();
      const idx = all.findIndex((e) => e.id === enrollmentId);
      if (idx >= 0) {
        all[idx].deletedAt = null;
        all[idx].deletedBy = null;
        saveDemoEnrollments(all);
      }
      return;
    }

    if (!db) throw new Error('Database not available');
    await updateDoc(doc(db, 'enrollments', enrollmentId), {
      deletedAt: null,
      deletedBy: null,
      updatedAt: Timestamp.fromDate(new Date()),
    });
  }

  /** Permanently delete an enrollment (admin only, use with caution) */
  async permanentDelete(enrollmentId: string): Promise<void> {
    if (isDemoMode) {
      const all = getDemoEnrollments().filter((e) => e.id !== enrollmentId);
      saveDemoEnrollments(all);
      return;
    }

    if (!db) throw new Error('Database not available');
    await deleteDoc(doc(db, 'enrollments', enrollmentId));
  }
}

export const enrollmentService = new EnrollmentService();
